import type { BlockyTimeDB } from '@/db/schema'
import { db as defaultDb } from '@/db/schema'
import type { SyncStateKey } from '@/db/types'

/**
 * Local-only key/value state. Never synced.
 *
 * The sync token lives here rather than in the settings blob, because settings
 * ARE synced -- round-tripping a credential through the API it authenticates
 * would be both pointless and a needless exposure. See 07-sync-protocol.md
 * section 3.
 */

export async function getSyncState(
  key: SyncStateKey,
  db: BlockyTimeDB = defaultDb,
): Promise<string | null> {
  const row = await db.syncState.get(key)
  return row?.value ?? null
}

export async function setSyncState(
  key: SyncStateKey,
  value: string,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await db.syncState.put({ key, value })
}

export async function clearSyncState(
  key: SyncStateKey,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await db.syncState.delete(key)
}

/**
 * This device's stable identity.
 *
 * Generated once and never changed. It breaks `updatedAt` ties during
 * conflict resolution, deterministically, so two devices reach the same answer
 * without coordinating.
 */
export async function getDeviceId(db: BlockyTimeDB = defaultDb): Promise<string> {
  const existing = await getSyncState('deviceId', db)
  if (existing) return existing

  const id = crypto.randomUUID()
  await setSyncState('deviceId', id, db)
  return id
}

export async function getLastPulledSeq(db: BlockyTimeDB = defaultDb): Promise<number> {
  const raw = await getSyncState('lastPulledSeq', db)
  const n = raw === null ? 0 : Number(raw)
  return Number.isFinite(n) ? n : 0
}

export async function setLastPulledSeq(
  seq: number,
  db: BlockyTimeDB = defaultDb,
): Promise<void> {
  await setSyncState('lastPulledSeq', String(seq), db)
}
